import React, { useState } from "react";
import { IcnCirclePlus, IcnClose } from "../../assets/icons";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { createWhitelistWallet } from "../../utils/whitelistService";
import { toast } from "react-toastify";
import { extractErrorMessage } from "../../utils/errorHandling";
import styles from "../../styles/AddWhitelistWalletModal.module.scss";

interface AddWhitelistWalletModalProps {
  onRefresh: () => void;
}

// Token options
const TOKEN_OPTIONS = [
  { value: "USDC", label: "USDC" }
];

// Chain options
const CHAIN_OPTIONS = [
  { value: "ETHEREUM_MAINNET", label: "Ethereum Mainnet" },
  { value: "POLYGON_MAINNET", label: "Polygon Mainnet" },
  { value: "POLYGON_AMOY", label: "Polygon Amoy" },
  { value: "ETHEREUM_TESTNET", label: "Ethereum Testnet" },
  { value: "OPTIMISM_SEPOLIA", label: "Optimism Sepolia" },
  { value: "OPTIMISM_MAINNET", label: "Optimism Mainnet" },
  { value: "BASE_MAINNET", label: "Base Mainnet" },
  { value: "BASE_SEPOLIA", label: "Base Sepolia" },
  { value: "SOLANA_MAINNET", label: "Solana Mainnet" },
  { value: "SOLANA_DEVNET", label: "Solana Devnet" }
];

// Validation schema
const validationSchema = Yup.object({
  label: Yup.string().required("Label is required"),
  walletAddress: Yup.string()
    .required("Wallet address is required")
    .matches(/^0x[a-fA-F0-9]{40}$/, "Please enter a valid Ethereum wallet address"),
  token: Yup.string().required("Token is required"),
  chain: Yup.string().required("Chain is required"),
  isCompliant: Yup.boolean().oneOf([true], "You must confirm that the wallet is KYC compliant")
});

interface FormValues {
  label: string;
  walletAddress: string;
  token: string;
  chain: string;
  isCompliant: boolean;
}

const AddWhitelistWalletModal: React.FC<AddWhitelistWalletModalProps> = ({ onRefresh }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleToggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleSubmit = async (values: FormValues, { resetForm }: any) => {
    setIsLoading(true);
    try {
      await createWhitelistWallet({
        label: values.label,
        wallet_address: values.walletAddress,
        token: values.token,
        chain: values.chain,
        is_compliant: values.isCompliant
      });
      toast.success(
        "Wallet added successfully! Please note: This wallet will undergo compliance review by Liquid Credit. The approval process may take a couple of days.",
        { autoClose: 8000 } // Show longer to ensure user reads the message
      );
      resetForm();
      handleToggleModal();
      onRefresh();
    } catch (error: any) {
      console.error("Error adding wallet to whitelist:", error);
      
      // Use the utility function to extract the error message
      const errorMessage = extractErrorMessage(error, "Failed to add wallet to whitelist");
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isModalOpen) {
    return (
      <button className={styles.addWalletBtn} onClick={handleToggleModal}>
        <IcnCirclePlus />
        Add Wallet
      </button>
    );
  }

  return (
    <>
      <button className={styles.addWalletBtn} onClick={handleToggleModal}>
        <IcnCirclePlus />
        Add Wallet
      </button>

      <div className={styles.modalOverlay} onClick={handleToggleModal}>
        <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h2>Add Wallet to Whitelist</h2>
            <button className={styles.modalClose} onClick={handleToggleModal}>
              <IcnClose />
            </button>
          </div>

          <Formik
            initialValues={{
              label: "",
              walletAddress: "",
              token: "USDC",
              chain: "",
              isCompliant: false
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, setFieldValue, isValid, dirty }) => (
              <Form className={styles.modalForm}>
                <div className={styles.formField}>
                  <label htmlFor="label" className={styles.fieldLabel}>
                    Label <span className={styles.fieldHint}>(e.g. Beneficiary Name)</span>
                  </label>
                  <input
                    id="label"
                    name="label"
                    type="text"
                    placeholder="Enter a descriptive label"
                    value={values.label}
                    onChange={(e) => setFieldValue('label', e.target.value)}
                    className={`${styles.textInput} ${errors.label && touched.label ? styles.inputError : ''}`}
                  />
                  {errors.label && touched.label && (
                    <span className={styles.errorText}>{errors.label}</span>
                  )}
                </div>

                <div className={styles.formField}>
                  <label htmlFor="walletAddress" className={styles.fieldLabel}>Wallet Address</label>
                  <input
                    id="walletAddress"
                    name="walletAddress"
                    type="text"
                    placeholder="0x1234567890abcdef1234567890abcdef12345678"
                    value={values.walletAddress}
                    onChange={(e) => setFieldValue('walletAddress', e.target.value)}
                    className={`${styles.textInput} ${styles.walletAddress} ${errors.walletAddress && touched.walletAddress ? styles.inputError : ''}`}
                  />
                  {errors.walletAddress && touched.walletAddress && (
                    <span className={styles.errorText}>{errors.walletAddress}</span>
                  )}
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formField}>
                    <label htmlFor="token" className={styles.fieldLabel}>Token</label>
                    <div className={styles.selectWrapper}>
                      <select
                        id="token"
                        name="token"
                        value={values.token}
                        onChange={(e) => setFieldValue('token', e.target.value)}
                        className={`${styles.selectField} ${errors.token && touched.token ? styles.inputError : ''}`}
                      >
                        <option value="">Select token</option>
                        {TOKEN_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {errors.token && touched.token && (
                      <span className={styles.errorText}>{errors.token}</span>
                    )}
                  </div>

                  <div className={styles.formField}>
                    <label htmlFor="chain" className={styles.fieldLabel}>Chain</label>
                    <div className={styles.selectWrapper}>
                      <select
                        id="chain"
                        name="chain"
                        value={values.chain}
                        onChange={(e) => setFieldValue('chain', e.target.value)}
                        className={`${styles.selectField} ${errors.chain && touched.chain ? styles.inputError : ''}`}
                      >
                        <option value="">Select chain</option>
                        {CHAIN_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {errors.chain && touched.chain && (
                      <span className={styles.errorText}>{errors.chain}</span>
                    )}
                  </div>
                </div>

                <div className={styles.formField}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="isCompliant"
                      checked={values.isCompliant}
                      onChange={(e) => setFieldValue('isCompliant', e.target.checked)}
                      className={styles.checkboxInput}
                    />
                    <span className={styles.checkboxCustom}></span>
                    <span className={styles.checkboxText}>Wallet is KYC compliant</span>
                  </label>
                  {errors.isCompliant && touched.isCompliant && (
                    <span className={styles.errorText}>{errors.isCompliant}</span>
                  )}
                </div>

                <div className={styles.complianceNotice}>
                  <div className={styles.noticeIcon}>ⓘ</div>
                  <div className={styles.noticeText}>
                    <strong>Compliance Review Required:</strong> After submission, this wallet will undergo compliance review by Liquid Credit. The approval process typically takes 1-3 business days.
                  </div>
                </div>

                <div className={styles.formActions}>
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.btnSecondary}`}
                    onClick={handleToggleModal}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`${styles.btn} ${styles.btnPrimary}`}
                    disabled={isLoading || !dirty || !isValid || !values.isCompliant}
                  >
                    {isLoading ? "Adding..." : "Add to Whitelist"}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default AddWhitelistWalletModal;
